<template>
  <div class="mt-4 space-y-4">
    <h3>{{ t('positions') }}</h3>
    <p v-if="invoiceType === InvoiceType.SELF_BILLING" class="flex items-center gap-2">
      <InfoIcon class="h-4 w-4" /> <span>{{ t('selfBillingInfo') }}</span>
    </p>
    <div class="rounded-md bg-white p-4">
      <div v-for="(position, i) in positionFields" :key="i" class="relative">
        <div class="mb-4 grid grid-cols-3 gap-4 border-b border-primary pb-4">
          <InputField :name="`positions[${i}].title`" :label="t('title')" class="col-span-3" />
          <InputField
            :name="`positions[${i}].amount`"
            :label="t('amount')"
            type="number"
            :max-fraction-digits="0"
          />
          <InputField
            :name="`positions[${i}].unitPrice`"
            :label="t('unitPrice')"
            type="number"
            :max-fraction-digits="2"
            adornment="€"
          />
          <SelectField
            :disabled="isTaxDisabled"
            :name="`positions[${i}].tax`"
            :label="t('tax')"
            type="number"
            :options="taxOptions"
          />
        </div>
        <div v-if="i !== 0" class="absolute right-0 top-0 flex items-center">
          <button
            class="cursor-pointer p-2 text-lg leading-3 text-red-600"
            @click="removePosition(i)"
          >
            ×
          </button>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <CVButton @click.prevent="pushPosition(getEmptyPosition())">{{
          t('addPosition')
        }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import { useI18n } from 'vue-i18n';
import { useFieldArray } from 'vee-validate';
import { InfoIcon } from 'lucide-vue-next';
import { TaxProcedure } from '@carvia/ros-client-types';
import { computed } from 'vue';
import { isNonStandardTaxProcedure, REGULAR_TAX_PERCENT } from '@/utils/tax-procedure';

export interface PositionFormValues {
  title: string;
  amount: number | undefined;
  unitPrice: number | undefined;
  tax: number | undefined;
}

const props = defineProps<{
  invoiceType: InvoiceType;
  taxProcedure?: TaxProcedure;
  countryTaxPercent?: number;
}>();

const { t } = useI18n();

const countryTaxPercent = computed(() => props.countryTaxPercent ?? REGULAR_TAX_PERCENT);

const taxOptions = computed(() => {
  const countryPercent = countryTaxPercent.value;
  const options = [{ label: '0%', value: 0 }];
  if (countryPercent !== 0) {
    options.push({ label: `${countryPercent}%`, value: countryPercent });
  }
  return options;
});

const isTaxDisabled = computed(
  () =>
    props.invoiceType === InvoiceType.DAMAGE ||
    props.invoiceType === InvoiceType.CANCELLATION_FEE ||
    isNonStandardTaxProcedure(props.taxProcedure ?? TaxProcedure.REGULAR),
);

const {
  push: pushPosition,
  remove: removePosition,
  fields: positionFields,
} = useFieldArray<PositionFormValues>('positions');

pushPosition(getEmptyPosition());

function getEmptyPosition(type?: InvoiceType): PositionFormValues {
  const invoiceType = type ?? props.invoiceType;
  return {
    title: '',
    amount: 1,
    unitPrice: undefined,
    tax:
      [InvoiceType.DAMAGE, InvoiceType.CANCELLATION_FEE].includes(invoiceType) ||
      isNonStandardTaxProcedure(props.taxProcedure ?? TaxProcedure.REGULAR)
        ? 0
        : countryTaxPercent.value,
  };
}
</script>

<i18n lang="json">
{
  "en": {
    "title": "Title",
    "amount": "Amount",
    "unitPrice": "Unit Price (Gross)",
    "tax": "Tax",
    "addPosition": "+ Add Position",
    "positions": "Positions",
    "selfBillingInfo": "Self-billing invoices must have a positive amount."
  },
  "de": {
    "title": "Titel",
    "amount": "Anzahl",
    "unitPrice": "Preis (Brutto)",
    "tax": "Steuer",
    "addPosition": "+ Position hinzufügen",
    "positions": "Positionen",
    "selfBillingInfo": "Der Gutschriftbetrag muss positiv angegeben werden."
  },
  "es": {
    "title": "Título",
    "amount": "Cantidad",
    "unitPrice": "Precio unitario (bruto)",
    "tax": "Impuesto",
    "addPosition": "+ Añadir línea",
    "positions": "Líneas",
    "selfBillingInfo": "Las facturas de autofacturación deben tener un importe positivo."
  }
}
</i18n>
